import { acceptHMRUpdate, defineStore } from 'pinia';
import { ref, reactive, computed, type Ref } from 'vue';
import {
    getCompanySettings,
    saveCompanySettings as saveCompanySettingsApi,
    deleteCompanyLogo,
} from '@/services/companySettingsService';
import {
    getSystemPreferences,
    saveSystemPreferences as saveSystemPreferencesApi,
} from '@/services/systemPreferencesService';
import type { SystemPreferences } from '@/services/systemPreferencesService';
import { ApiError } from '@/models/errors/ApiError';

export interface CompanySettings {
    companyName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    email: string;
    phone: string;
    fax: string;
    logoUrl: string;
}

const EMPTY_COMPANY_SETTINGS: CompanySettings = {
    companyName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    phone: '',
    fax: '',
    logoUrl: '',
};

const EMPTY_SYSTEM_PREFERENCES: SystemPreferences = {
    paymentsLowBalanceSettings: null,
    paymentsOutstandingBalanceSettings: null,
    negotiationsDefaultSettlementOffer: null,
    negotiationsDefaultNumberOfPayments: null,
};

export const useSettingsStore = defineStore('settings', () => {
    function applyData<T extends object>(
        target: T,
        snapshot: Ref<T | null>,
        data: T,
    ) {
        (Object.keys(data) as (keyof T)[]).forEach((key) => {
            target[key] = data[key];
        });
        snapshot.value = { ...data };
    }

    // ── Company Settings ────────────────────────────────────────────────
    const form = reactive<CompanySettings>({ ...EMPTY_COMPANY_SETTINGS });
    const originalData = ref<CompanySettings | null>(null);
    const loading = ref(false);
    const saving = ref(false);
    const logoFile = ref<File | null>(null);
    const previewLogoUrl = ref<string>('');
    const logoMarkedForDeletion = ref(false);

    const companyName = computed(() => originalData.value?.companyName ?? '');

    const hasCompanySettingsChanges = computed(() => {
        if (!originalData.value) return false;
        return (Object.keys(EMPTY_COMPANY_SETTINGS) as (keyof CompanySettings)[])
            .some((key) => form[key] !== originalData.value![key]);
    });

    const isDirty = computed(() => hasCompanySettingsChanges.value || logoFile.value !== null || logoMarkedForDeletion.value);

    const displayLogoUrl = computed(() => {
        if (previewLogoUrl.value) return previewLogoUrl.value;
        if (logoMarkedForDeletion.value) return '';
        return form.logoUrl;
    });

    function clearLogoFile() {
        if (previewLogoUrl.value) {
            URL.revokeObjectURL(previewLogoUrl.value);
        }

        logoFile.value = null;
        previewLogoUrl.value = '';
    }

    function markLogoForDeletion() {
        clearLogoFile();
        logoMarkedForDeletion.value = true;
    }

    function setLogoFile(file: File) {
        clearLogoFile();
        logoMarkedForDeletion.value = false;
        logoFile.value = file;
        previewLogoUrl.value = URL.createObjectURL(file);
    }

    async function fetchCompanySettings() {
        loading.value = true;
        try {
            const data = await getCompanySettings();
            applyData(form, originalData, data);
        } catch (error) {
            // 404 means no settings saved yet — seed with empty defaults so isDirty works
            if (error instanceof ApiError && error.status === 404) {
                applyData(form, originalData, { ...EMPTY_COMPANY_SETTINGS });
            } else {
                throw error;
            }
        } finally {
            loading.value = false;
        }
    }

    async function saveCompanySettings() {
        saving.value = true;
        try {
            if (hasCompanySettingsChanges.value || logoFile.value) {
                const result = await saveCompanySettingsApi({ ...form }, logoFile.value);
                applyData(form, originalData, result);
                clearLogoFile();
            }

            if (logoMarkedForDeletion.value) {
                if (form.logoUrl) {
                    await deleteCompanyLogo();
                }

                logoMarkedForDeletion.value = false;
                form.logoUrl = '';
                if (originalData.value) originalData.value.logoUrl = '';
            }
        } finally {
            saving.value = false;
        }
    }

    function resetForm() {
        if (!originalData.value) return;
        applyData(form, originalData, originalData.value);
        clearLogoFile();
        logoMarkedForDeletion.value = false;
    }

    // ── System Preferences ──────────────────────────────────────────────
    const spForm = reactive<SystemPreferences>({ ...EMPTY_SYSTEM_PREFERENCES });
    const spOriginalData = ref<SystemPreferences | null>(null);
    const spLoading = ref(false);
    const spSaving = ref(false);

    const spIsDirty = computed(() => {
        if (!spOriginalData.value) return false;
        return (Object.keys(spForm) as (keyof SystemPreferences)[])
            .some((key) => spForm[key] !== spOriginalData.value![key]);
    });

    async function fetchSystemPreferences() {
        spLoading.value = true;
        try {
            const data = await getSystemPreferences();
            applyData(spForm, spOriginalData, data);
        } catch (error) {
            // 404 means no company_settings row yet — seed with empty defaults so isDirty works
            if (error instanceof ApiError && error.status === 404) {
                applyData(spForm, spOriginalData, { ...EMPTY_SYSTEM_PREFERENCES });
            } else {
                throw error;
            }
        } finally {
            spLoading.value = false;
        }
    }

    async function saveSystemPreferences() {
        spSaving.value = true;
        try {
            const result = await saveSystemPreferencesApi({ ...spForm });
            applyData(spForm, spOriginalData, result);
        } finally {
            spSaving.value = false;
        }
    }

    function resetSpForm() {
        if (!spOriginalData.value) return;
        applyData(spForm, spOriginalData, spOriginalData.value);
    }

    return {
        // Company Settings
        form,
        originalData,
        loading,
        saving,
        logoFile,
        companyName,
        isDirty,
        displayLogoUrl,
        fetchCompanySettings,
        saveCompanySettings,
        resetForm,
        clearLogoFile,
        setLogoFile,
        markLogoForDeletion,
        // System Preferences
        spForm,
        spOriginalData,
        spLoading,
        spSaving,
        spIsDirty,
        fetchSystemPreferences,
        saveSystemPreferences,
        resetSpForm,
    };
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSettingsStore, module.hot));
}
